import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";
import IntlTelInput from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";

import { PopupModal } from "../../UI/Modal/Modal";
import { createContact, updateContact, resetCreateContact, resetUpdateContact } from "../../../actions/addressBookAction";
import { EMAIL_PATTERN_REGEX, allowedCountries, allowedCountriesDialCodePattern, NOT_SUPPORTED_COUNTRY_CODES, UK_COUNTRY_CODE_NEW, UK_COUNTRY_CODE_OLD, DefaultLanguage } from "../../../constants/Constants";
import AllCountries from "react-intl-tel-input/dist/components/AllCountries";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";

// Build a static dial-code → ISO map from IntlTelInput's country data at module load time.
// When multiple countries share a dial code (e.g. +1 for US/Canada), the first entry
// (lowest priority index) is used, which is the primary country for that code.
// This avoids relying on window.intlTelInputGlobals which is only available after an
// IntlTelInput component has mounted at least once.
const _dialCodeToIso = {};
AllCountries.getCountries().forEach((c) => {
  if (!_dialCodeToIso[c.dialCode]) {
    _dialCodeToIso[c.dialCode] = c.iso2;
  }
});
// Dial code 44 is shared by UK (gb), Guernsey (gg), Jersey (je), Isle of Man (im).
// AllCountries lists Guernsey first (alphabetical), but gg is not in allowedCountries.
// Explicitly pin 44 → gb so getCountryIsoByDialCode("44") always returns the correct flag.
_dialCodeToIso["44"] = "gb";
// Dial code 1 is shared across the NANP (US, Canada, and 20+ Caribbean countries).
// AllCountries iteration order lands on a non-US country first, so pin +1 → us as the
// default flag when a stored contact has a missing/invalid ISO2. (When the stored ISO2
// is valid — e.g. "ca" for a Canadian contact — the resolver below trusts it and skips
// this fallback, preserving the country the user chose at save time.)
_dialCodeToIso["1"] = "us";

const getCountryIsoByDialCode = (dialCode) => _dialCodeToIso[String(dialCode)] || null;

// Mirrors SendMsg's custom area-code check: libphonenumber (via IntlTelInput) rejects
// some legitimate US/AU area codes (e.g. US 689). This override accepts any 10-digit
// US number — or 9/10-digit AU number — with an area code in 200–999.
const isPhoneNumberValidForCustomAreaCode = (countryCode, phoneNumber) => {
  if (!phoneNumber) return false;
  if (NOT_SUPPORTED_COUNTRY_CODES.indexOf(countryCode) < 0) return false;
  const digits = phoneNumber.replace(/\D+/g, "");
  if (countryCode == "61" && digits.length < 9) return false;
  if (countryCode != "61" && digits.length !== 10) return false;
  const areaCode = phoneNumber.substring(0, 3);
  return areaCode >= 200 && areaCode <= 999;
};

const AddEditContactModal = ({ isOpen, contact, onClose, onSuccess, initialPhone, initialDialCode, initialCountryCode, initialEmail }) => {
  const dispatch = useDispatch();
  const {
    createContactLoading,
    createContactData,
    createContactError,
    updateContactLoading,
    updateContactData,
    updateContactError
  } = useSelector((state) => state.addressBook);

  const { getCountriesData } = useSelector((state) => state.sessionList);
  const langId = useSelector((state) => state.language?.selectedlanguage?.id);
  const [localisedValues, setLocalisedValues] = useState(Localize[langId] || Localize[DefaultLanguage]);
  const ariaLocalised = AriaLocalize[langId] || AriaLocalize['en_US'];

  useEffect(() => {
    if (langId) {
      setLocalisedValues(Localize[langId] || Localize[DefaultLanguage]);
    }
  }, [langId]);
  const convertCountryCode = (code) => code === UK_COUNTRY_CODE_NEW ? UK_COUNTRY_CODE_OLD : code;
  const defaultCountryIso = Array.isArray(getCountriesData)
    ? convertCountryCode(getCountriesData.find((c) => c.isDefault === true)?.countryCode?.toLowerCase() || "us")
    : "us";


  const isEditMode = !!contact;
  const focusPhoneFirst = isEditMode && !!contact.phone;
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  // Incremented each time form data is initialized so IntlTelInput remounts with the
  // correct defaultCountry. Using a ref (not state) avoids an extra render cycle.
  const phoneInputKeyRef = useRef(0);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    countryCode: "",
    dialCode: "",
    email: "",
    company: "",
    notes: "",
    isFavorite: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [addAnother, setAddAnother] = useState(false);
  const initialFormData = useRef(null);

  // Initialize form with contact data if editing, or prefill from props
  useEffect(() => {
    if (contact) {
      let phone = contact.phone || "";
      const rawDialCode = contact.dialCode || "";

      // Normalize dial code to include "+" for comparison (API returns "+91" but guard either way)
      const normalizedDialCode = rawDialCode
        ? (rawDialCode.startsWith("+") ? rawDialCode : `+${rawDialCode}`)
        : "";

      // If the stored phone already contains the country code prefix (e.g. "+919100798028"),
      // strip it so IntlTelInput only receives the local number (e.g. "9100798028")
      if (phone.startsWith("+") && normalizedDialCode) {
        if (phone.startsWith(normalizedDialCode)) {
          phone = phone.substring(normalizedDialCode.length);
        }
      }

      // dialCode without "+" — IntlTelInput stores/returns digits only (e.g. "91")
      const dialCodeDigits = rawDialCode.replace(/^\+/, "");

      // Resolve the country ISO for IntlTelInput's defaultCountry.
      // Trust the stored countryCode when it's a real ISO2 in our allowed list — this
      // preserves the US vs Canada (and other NANP) distinction for +1 numbers, which
      // dial-code lookup can't disambiguate. Fall back to dial-code lookup only when
      // the stored value is missing or malformed (e.g. imports storing numeric values
      // like "45" instead of "dk"). Convert stored "uk" → "gb" at the end for legacy rows.
      const storedCountryCode = (contact.countryCode || "").toLowerCase();
      const isStoredIsoValid = storedCountryCode && allowedCountries.includes(storedCountryCode);
      const derivedCountryCode = isStoredIsoValid
        ? storedCountryCode
        : (getCountryIsoByDialCode(dialCodeDigits) || storedCountryCode);
      const countryCode = derivedCountryCode === UK_COUNTRY_CODE_NEW ? UK_COUNTRY_CODE_OLD : derivedCountryCode;

      const initial = {
        name: contact.name || "",
        phone,
        countryCode,
        dialCode: dialCodeDigits,
        email: contact.email || "",
        company: contact.company || "",
        notes: contact.notes || "",
        isFavorite: contact.isFavorite || false,
      };
      initialFormData.current = initial;
      phoneInputKeyRef.current += 1;
      setFormData(initial);
      setTouched({});
      setErrors({});
      setIsPhoneValid(true);

      // If the stored phone is invalid, show the error immediately so the user
      // knows why Save Changes is disabled (rather than silently showing a grey button).
      if (phone) {
        const phoneError = validateField("phone", phone);
        if (phoneError) {
          setTouched({ phone: true });
          setErrors({ phone: phoneError });
          setIsPhoneValid(false);
        }
      }
    } else if (isOpen) {
      phoneInputKeyRef.current += 1;
      setFormData({
        name: "",
        phone: initialPhone || "",
        countryCode: initialCountryCode || "",
        dialCode: initialDialCode || "",
        email: initialEmail || "",
        company: "",
        notes: "",
        isFavorite: false,
      });
      setErrors({});
      setTouched({});
      setIsPhoneValid(true);
      setAddAnother(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, isOpen, initialPhone, initialDialCode, initialCountryCode, initialEmail]);

  // Handle success
  useEffect(() => {
    if (createContactData && !isEditMode) {
      dispatch(resetCreateContact());
      const wasFavorite = formData.isFavorite;
      if (addAnother) {
        // Reset form for next contact
        setFormData({
          name: "",
          phone: "",
          countryCode: "",
          dialCode: "",
          email: "",
          company: "",
          notes: "",
          isFavorite: false,
        });
        setErrors({});
        setTouched({});
        setIsPhoneValid(true);
        onSuccess({ addAnother: true, isFavorite: wasFavorite, savedContact: createContactData });
        // Focus the name input for quick entry
        setTimeout(() => {
          nameInputRef.current?.focus();
        }, 100);
      } else {
        onSuccess({ addAnother: false, isFavorite: wasFavorite, savedContact: createContactData });
      }
    }
  }, [createContactData, isEditMode, dispatch, onSuccess, addAnother]);

  useEffect(() => {
    if (updateContactData && isEditMode) {
      dispatch(resetUpdateContact());
      onSuccess();
    }
  }, [updateContactData, isEditMode, dispatch, onSuccess]);

  // Handle errors
  useEffect(() => {
    if (createContactError) {
      setErrors((prev) => ({ ...prev, submit: createContactError.message || localisedValues['error_occurred'] }));
    }
  }, [createContactError, localisedValues]);

  useEffect(() => {
    if (updateContactError) {
      setErrors((prev) => ({ ...prev, submit: updateContactError.message || localisedValues['error_occurred'] }));
    }
  }, [updateContactError, localisedValues]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(resetCreateContact());
      dispatch(resetUpdateContact());
    };
  }, [dispatch]);

  // Validation
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) {
          return localisedValues['name_required'];
        }
        if (value.trim().length < 1) {
          return localisedValues['name_min_length'];
        }
        if (value.trim().length > 255) {
          return localisedValues['max_chars'].replace('{n}', 255);
        }
        return "";

      case "company":
        if (value.trim().length > 255) {
          return localisedValues['max_chars'].replace('{n}', 255);
        }
        return "";

      case "email":
        if (value && !EMAIL_PATTERN_REGEX.test(value)) {
          return localisedValues['invalid_email'];
        }
        return "";

      case "phone":
        if (value.trim() && !/^[+]?[\d\s\-()]{7,20}$/.test(value.replace(/\s/g, ""))) {
          return localisedValues['invalid_phone'];
        }
        return "";

      case "notes":
        if (value.trim().length > 1000) {
          return localisedValues['max_chars'].replace('{n}', 1000);
        }
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    // At least one of phone or email is required
    if (!formData.phone.trim() && !formData.email.trim()) {
      newErrors.phone = localisedValues['phone_or_email_required'];
      newErrors.email = localisedValues['phone_or_email_required'];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate on change for max length fields
    const error = validateField(name, type === "checkbox" ? checked : value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
      submit: "",
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async () => {
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        await dispatch(updateContact(contact.contactId, formData));
      } else {
        await dispatch(createContact(formData));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, submit: error.message || localisedValues['error_occurred'] }));
    }
  };

  const handleClose = () => {
    dispatch(resetCreateContact());
    dispatch(resetUpdateContact());
    onClose();
  };

  const isLoading = createContactLoading || updateContactLoading;
  const phoneEntered = formData.phone.trim().length > 0;
  const emailEntered = formData.email.trim().length > 0;
  const hasPhone = phoneEntered && formData.phone.trim().length >= 7 && isPhoneValid;
  const hasEmail = emailEntered && EMAIL_PATTERN_REGEX.test(formData.email);
  const isPhoneOk = !phoneEntered || hasPhone;
  const isEmailOk = !emailEntered || hasEmail;
  const hasChanges = !isEditMode || !initialFormData.current || Object.keys(initialFormData.current).some(
    (key) => formData[key] !== initialFormData.current[key]
  );
  const isFormValid = formData.name.trim().length >= 1 && formData.name.trim().length <= 255 && formData.company.trim().length <= 255 && formData.notes.trim().length <= 1000 && isPhoneOk && isEmailOk && (hasPhone || hasEmail) && hasChanges;

  // Phone input handlers - matching SendMsg pattern
  const handlePhoneChange = (isValid, value, countryData) => {
    // Clean phone number - remove non-digit characters except for formatting
    let phoneNumber = value;
    const patternMob = /[a-zA-Z`~!@$%^&_|=?;:'" ,<>{}\\/]/gi;
    const isNonValid = patternMob.test(phoneNumber);
    if (isNonValid) {
      phoneNumber = phoneNumber.replace(patternMob, "");
    }

    let matchedCountryCode = phoneNumber.match(allowedCountriesDialCodePattern);
    if (matchedCountryCode && matchedCountryCode.length > 0) {
      const newValueIndex = phoneNumber.lastIndexOf("+");
      phoneNumber = phoneNumber.substring(newValueIndex);
      matchedCountryCode = phoneNumber.match(allowedCountriesDialCodePattern);
      phoneNumber = phoneNumber.substring(matchedCountryCode[0]?.length);
    }

    setFormData((prev) => ({
      ...prev,
      phone: phoneNumber,
      countryCode: countryData?.iso2 || "",
      dialCode: countryData?.dialCode || "",
    }));

    // Check validity using the ref if available
    const isValidNumber = phoneInputRef.current?.isValidNumber?.(value) ?? isValid;
    const isValidForCustomAreaCode = isPhoneNumberValidForCustomAreaCode(
      countryData?.dialCode,
      phoneInputRef.current?.getNumber?.(value, "-")
    );
    setIsPhoneValid(isValidNumber || isValidForCustomAreaCode);

    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "", submit: "" }));
    }
  };

  const handlePhoneBlur = (isValid, value) => {
    setTouched((prev) => ({ ...prev, phone: true }));
    const checkForError = value?.replace(/\D+/g, "").length !== 0;

    if (checkForError) {
      const isValidNumber = phoneInputRef.current?.isValidNumber?.(value) || isValid;
      const dialCode = phoneInputRef.current?.selectedCountryData?.dialCode;
      const isValidForCustomAreaCode = isPhoneNumberValidForCustomAreaCode(
        dialCode,
        phoneInputRef.current?.getNumber?.(value, "-")
      );
      const isPhoneOk = isValidNumber || isValidForCustomAreaCode;
      setIsPhoneValid(isPhoneOk);

      if (!isPhoneOk && formData.phone.trim()) {
        setErrors((prev) => ({ ...prev, phone: localisedValues['invalid_phone'] }));
      }
    }
  };

  const handlePhoneFocus = () => {
    setIsPhoneValid(true);
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleSelectFlag = (_currentNumber, countryData, _fullNumber, isValid) => {
    setFormData((prev) => ({
      ...prev,
      countryCode: countryData?.iso2 || "",
      dialCode: countryData?.dialCode || "",
    }));
    const isValidForCustomAreaCode = isPhoneNumberValidForCustomAreaCode(
      countryData?.dialCode,
      phoneInputRef.current?.getNumber?.()
    );
    setIsPhoneValid(isValid || isValidForCustomAreaCode);
  };

  // Header content
  const headerContent = (
    <div className="modal-title-box">
      <h2 className="modal-title">
        {isEditMode ? localisedValues['edit_contact'] : localisedValues['add_contact']}
      </h2>
    </div>
  );

  // Body content
  const bodyContent = (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} noValidate className="contact-form">
      <p className="form-hint required-legend"><span className="required" aria-hidden="true">*</span> {localisedValues['required_field']}</p>
      {/* Row 1: Name & Company */}
      <div className="form-row">
        <div className={clsx("form-group", errors.name && touched.name && "has-error")}>
          <label htmlFor="name" className="form-label">
            {localisedValues['name']} <span className="required" aria-hidden="true">*</span>
          </label>
          <input
            ref={nameInputRef}
            data-autofocus={!focusPhoneFirst || undefined}
            type="text"
            id="name"
            name="name"
            className={clsx("form-control", errors.name && touched.name && "is-invalid")}
            value={formData.name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={localisedValues['enter_name']}
            data-testid="nameInput"
            autoComplete="name"
            aria-invalid={Boolean(errors.name && touched.name) || undefined}
            aria-describedby={errors.name && touched.name ? "name-error" : undefined}
            aria-required="true"
          />
          {errors.name && touched.name && (
            <div id="name-error" role="alert" className="invalid-feedback">{errors.name}</div>
          )}
        </div>
        <div className={clsx("form-group", errors.company && touched.company && "has-error")}>
          <label htmlFor="company" className="form-label">
            {localisedValues['company']}
          </label>
          <input
            type="text"
            id="company"
            name="company"
            className={clsx("form-control", errors.company && touched.company && "is-invalid")}
            value={formData.company}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={localisedValues['enter_company']}
            data-testid="companyInput"
            autoComplete="organization"
            aria-invalid={Boolean(errors.company && touched.company) || undefined}
            aria-describedby={errors.company && touched.company ? "company-error" : undefined}
          />
          {errors.company && touched.company && (
            <div id="company-error" role="alert" className="invalid-feedback">{errors.company}</div>
          )}
        </div>
      </div>

      {/* Row 2: Phone & Email */}
      <div className="form-row">
        <div className={clsx("form-group phone-input-group", errors.phone && touched.phone && "has-error")}>
          <label htmlFor="phone" className="form-label">
            {localisedValues['phone']}
          </label>
          <IntlTelInput
            key={`${defaultCountryIso}-${phoneInputKeyRef.current}`}
            ref={phoneInputRef}
            fieldId="phone"
            data-testid="phoneInput"
            defaultCountry={formData.countryCode || defaultCountryIso}
            value={formData.phone}
            containerClassName="intl-tel-input"
            inputClassName={clsx("form-control", errors.phone && touched.phone && "is-invalid")}
            separateDialCode={true}
            onlyCountries={allowedCountries}
            autoHideDialCode={true}
            placeholder={localisedValues['enter_phone']}
            onPhoneNumberChange={handlePhoneChange}
            onPhoneNumberBlur={handlePhoneBlur}
            onPhoneNumberFocus={handlePhoneFocus}
            onSelectFlag={handleSelectFlag}
            useMobileFullscreenDropdown={false}
            telInputProps={{
              autoComplete: "tel-national",
              "data-autofocus": focusPhoneFirst || undefined,
              "aria-invalid": Boolean(errors.phone && touched.phone) || undefined,
              "aria-describedby": errors.phone && touched.phone ? "phone-error phone-hint" : "phone-hint",
            }}
          />
          {errors.phone && touched.phone && (
            <div id="phone-error" role="alert" className="invalid-feedback">{errors.phone}</div>
          )}
          <div id="phone-hint" className="form-hint">{localisedValues['phone_or_email_required']}</div>
        </div>
        <div className={clsx("form-group", errors.email && touched.email && "has-error")}>
          <label htmlFor="email" className="form-label">
            {localisedValues['email']}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={clsx("form-control", errors.email && touched.email && "is-invalid")}
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={localisedValues['enter_email']}
            maxLength={254}
            data-testid="emailInput"
            autoComplete="email"
            aria-invalid={Boolean(errors.email && touched.email) || undefined}
            aria-describedby={errors.email && touched.email ? "email-error" : undefined}
          />
          {errors.email && touched.email && (
            <div id="email-error" role="alert" className="invalid-feedback">{errors.email}</div>
          )}
        </div>
      </div>

      {/* Row 3: Notes */}
      <div className="form-row notes-row">
        <div className={clsx("form-group", errors.notes && touched.notes && "has-error")}>
          <label htmlFor="notes" className="form-label">
            {localisedValues['notes']}
          </label>
          <textarea
            id="notes"
            name="notes"
            className={clsx("form-control", errors.notes && touched.notes && "is-invalid")}
            value={formData.notes}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={localisedValues['notes_placeholder']}
            rows={3}
            data-testid="notesInput"
            aria-invalid={Boolean(errors.notes && touched.notes) || undefined}
            aria-describedby={errors.notes && touched.notes ? "notes-error" : undefined}
          />
          {errors.notes && touched.notes && (
            <div id="notes-error" role="alert" className="invalid-feedback">{errors.notes}</div>
          )}
        </div>
      </div>

      {/* Checkbox - Left aligned */}
      <div className="form-group checkbox-row">
        <div className="checkbox">
          <label>
            <input
              type="checkbox"
              id="isFavorite"
              name="isFavorite"
              checked={formData.isFavorite}
              onChange={handleInputChange}
              data-testid="favoriteCheckbox"
            />
            <span className="for-design"></span>
            <span className="icon-star-filled favorite-icon" aria-hidden="true"></span>
            {localisedValues['mark_as_favorite']}
          </label>
        </div>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div role="alert" className="validation-error">{errors.submit}</div>
      )}
    </form>
  );

  // Footer content
  const footerContent = (
    <>
      {!isEditMode && !initialPhone && !initialEmail && (
        <div className="checkbox footer-checkbox">
          <label>
            <input
              type="checkbox"
              id="addAnother"
              checked={addAnother}
              onChange={(e) => setAddAnother(e.target.checked)}
              data-testid="addAnotherCheckbox"
            />
            <span className="for-design"></span>
            {localisedValues['add_another_after_save']}
          </label>
        </div>
      )}
      <div className="button">
        <button type="button" className="btn btn-outline-secondary font-weight-500" onClick={handleClose} data-testid="modalCloseButton" aria-label={isEditMode ? ariaLocalised.cancel_editing_contact : ariaLocalised.cancel_adding_contact}>{localisedValues['cancel']}</button>
      </div>
      <div className="button">
        <button type="button" className="btn btn-primary font-weight-500 btn-with-icon" onClick={handleSubmit} disabled={isLoading || !isFormValid} data-testid="modalSendButton">
          {isLoading && <span className="btn-loader-icon"><span className="for-icon icon-loader spin" aria-hidden="true"></span></span>}
          {isEditMode ? localisedValues['save_changes'] : localisedValues['add_contact']}
        </button>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <PopupModal
      modalClass="contact-modal"
      dialogClass="modal-dialog-centered"
      headerContent={headerContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
      closeModal={handleClose}
      backdrop="static"
      disablekeyboard={isLoading}
      footerInlineClass="contact-modal-footer"
    />
  );
};

AddEditContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  contact: PropTypes.shape({
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    countryCode: PropTypes.string,
    dialCode: PropTypes.string,
    email: PropTypes.string,
    company: PropTypes.string,
    notes: PropTypes.string,
    isFavorite: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  initialPhone: PropTypes.string,
  initialDialCode: PropTypes.string,
  initialCountryCode: PropTypes.string,
  initialEmail: PropTypes.string,
};

AddEditContactModal.defaultProps = {
  contact: null,
  initialPhone: "",
  initialDialCode: "",
  initialCountryCode: "",
  initialEmail: "",
};

export default AddEditContactModal;
